import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import { genericSubmitHandler } from 'components/organisms/FormsSlice/Forms.helper';
import Copytext from 'components/molecules/Copytext/Copytext';
import { CheckboxInput } from 'components/atoms/CheckboxInput';
import Column from 'components/layout/Grid/Column';
import {
  FormColumn,
  FormError,
  HeadlineLabel,
  InputSubmit,
  InputTextArea,
  StyledLabel,
} from 'components/organisms/FormsSlice/Forms/Forms';
import { GruppenReservationFormInputTypes, FormParentProps } from './GruppenReservationForm.types';
import { toast } from 'react-toastify';
import { Loading } from 'components/atoms/Loading';
import { FormSuccessNotification } from 'components/molecules/FormSuccessNotification';
import { RichText } from 'components/atoms/RichText';
import { isRichTextEmpty } from 'prismic/helpers/isRichTextEmpty';
import { TextInput } from 'components/atoms/TextInput';
import { Heading } from 'components/atoms/Heading';
import { FormDatePicker } from 'components/atoms/FormDatePicker';
import { SelectInput } from 'components/atoms/SelectInput';
import { RadioInput } from 'components/atoms/RadioInput';
import { validationOptions } from 'components/organisms/FormsSlice/Forms/validationOptions';
import {
  DateGroupStyled,
  WrapperRBArrivalPlace,
  WrapperRBNumberOfEntries,
  WrapperRBSalutations,
} from './GruppenReservationForm.styles';
import { DomainContext } from 'contexts/domain/DomainContext';
import getNewsletterURL from 'helpers/forms/getNewsletterURL';
import { SalutationEnum } from 'helpers/forms/forms.types';
import getHeadingSkin from 'helpers/getHeadingSkin';
import getHeadingTag from 'helpers/getHeadingTag';

const defaultValues = {
  country: 'CH',
  language: 'de',
};

export const GruppenReservationForm: React.FC<FormParentProps> = ({
  introRichText,
  titleNotification,
  textNotification,
}) => {
  const { t, lang } = useTranslation('common');
  const [showForm, setShowForm] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<Date | null>(new Date());

  const {
    setValue,
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm<GruppenReservationFormInputTypes>({
    defaultValues,
  });
  useEffect(() => {
    setValue('country', 'CH');
    setValue('language', 'de');
  }, [setValue]);

useEffect(() => {
  register("programId");
  register("programName");
}, [register]);

  const selectedGroupType = watch('groupTyp');
  const selectedExperience = watch("experienceType");

  useEffect(() => {
    if (selectedGroupType === 'Privat') {
      setValue('nameGroup', '');
    }
  }, [selectedGroupType, setValue]);

  const {
    state: { domain },
  } = useContext(DomainContext);

  const isIntroRichTextEmpty = isRichTextEmpty(introRichText);
  const urlNewsletter = getNewsletterURL(domain, lang);

  const submitHandler = async (data: GruppenReservationFormInputTypes) => {
    const hasPhone = data.phone && data.phone.trim() !== '';
    const hasMobile = data.mobilePhone && data.mobilePhone.trim() !== '';

    // kontrollo që të paktën një të jetë mbushur
    if (!hasPhone && !hasMobile) {
      setError('phone', {
        type: 'manual',
        message: t('contactForm.errors.phoneOrMobileRequired'),
      });

      const phoneField = document.querySelector('input[name="phone"]');
      if (phoneField) {
        phoneField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (phoneField as HTMLElement).focus();
      }

      return;
    }

    try {
      const response = await genericSubmitHandler({ data });

      if (!response.data.ok) {
        toast.error(t('forms.responses.400'));
        return setShowForm(true);
      }

      setShowForm(false);
    } catch (error) {
      setShowForm(true);
      console.error('Error submitting form:', error);
    }
  };

  const onClickReturn = (): void => {
    reset();
    setShowForm(true);
  };

  const optionsGroupTyp = [
    { value: 'Privat', label: t('groupReservationForm.labels.groupTyp.option1') },
    { value: 'Firma/Verein/Institution', label: t('groupReservationForm.labels.groupTyp.option2') },
  ];
  useEffect(() => {
    const calculatedDate = new Date();
    calculatedDate.setDate(calculatedDate.getDate() + 4);
    setStartDate(calculatedDate);
    setValue('visitDate', calculatedDate);
  }, [setValue]);


  const experienceTypeObject: Record<string, string> = 
  t('groupReservationForm.labels.experienceType', {}, { returnObjects: true });

useEffect(() => {
  if (!selectedExperience) return;

  const programId = selectedExperience;
  const programName = experienceTypeObject[selectedExperience];

  setValue("programId", programId, { shouldValidate: true });
  setValue("programName", programName, { shouldValidate: true });
}, [selectedExperience, setValue, experienceTypeObject]);


const optionsExperienceType = Object.entries(experienceTypeObject).map(
  ([id, label]) => ({
    value: id,    
    label: label,  
  })
);



  const generateTimeGridOptions = () => {
    const hours = ['09', '10', '11', '12', '13', '14', '15', '16', '17']; // Nga 09 deri 17
    const minutes = ['00', '15', '30', '45'];
    const options = [];

    for (let h of hours) {
      for (let m of minutes) {
        if (h === '17' && m !== '00') break;
        options.push({
          value: `${h}:${m}`,
          label: `${h} : ${m}`,
        });
      }
    }

    return options;
  };

  return (
    <>
      {!showForm && (
        <Column span={12}>
          <FormColumn span={12}>
            <FormSuccessNotification
              onClickReturn={onClickReturn}
              titleNotification={titleNotification}
              textNotification={textNotification}
            />
          </FormColumn>
        </Column>
      )}
      {showForm && (
        <>
          {!isIntroRichTextEmpty && (
            <FormColumn span={12}>
              <RichText rich_text={introRichText} />
            </FormColumn>
          )}
          <Column span={12}>
            <form onSubmit={handleSubmit(submitHandler)}>
              <FormColumn span={12}>
                <HeadlineLabel>{t('tableReservationForm.labels.generalInfo')}</HeadlineLabel>
              </FormColumn>
              <FormColumn span={12}>
                <SelectInput
                  options={optionsGroupTyp}
                  label="groupReservationForm.labels.groupTypTitle"
                  placeholder="groupReservationForm.placeholders.selectGroupTyp"
                  name="groupTyp"
                  errorMessageRequired="groupReservationForm.errors.groupTypRequired"
                  hasError={errors.groupTyp !== undefined}
                  register={register}
                  validationOptions={validationOptions.groupTyp}
                />
              </FormColumn>
              <FormColumn span={12}>
                <TextInput
                  hasError={errors.nameGroup !== undefined}
                  label={
                    selectedGroupType === 'Firma/Verein/Institution'
                      ? `${t('groupReservationForm.labels.nameGroup')} *`
                      : t('groupReservationForm.labels.nameGroup')
                  }
                  name="nameGroup"
                  placeholder={t('groupReservationForm.placeholders.nameGroup')}
                  errorMessageRequired={t('groupReservationForm.errors.nameGroupRequired')}
                  register={register}
                  validationOptions={
                    selectedGroupType === 'Firma/Verein/Institution' ? { required: true } : {}
                  }
                />

                <Heading skin={getHeadingSkin('heading3')} elementType={getHeadingTag('heading3')}>
                  {t('groupReservationForm.labels.yourInformation')}
                </Heading>

                <WrapperRBSalutations>
                  <RadioInput
                    id="salutationFemale"
                    name="salutation"
                    register={register}
                    translation="contactForm.labels.salutation.female"
                    value={SalutationEnum.female}
                  />
                  <RadioInput
                    id="salutationMale"
                    name="salutation"
                    register={register}
                    translation="contactForm.labels.salutation.male"
                    value={SalutationEnum.male}
                    validationOptions={validationOptions.salutation}
                  />
                  <RadioInput
                    id="salutationNone"
                    name="salutation"
                    register={register}
                    translation="contactForm.labels.salutation.none"
                    value={SalutationEnum.none}
                  />
                </WrapperRBSalutations>
                {errors.salutation && isSubmitted && (
                  <FormError className="salutationError">
                    {t('contactForm.errors.salutationRequired')}
                  </FormError>
                )}
              </FormColumn>
              <FormColumn span={12}>
                <TextInput
                  errorMessageRequired="contactForm.errors.firstNameRequired"
                  hasError={errors.firstName !== undefined}
                  label="contactForm.labels.firstName"
                  name="firstName"
                  placeholder="contactForm.placeholders.firstName"
                  register={register}
                  validationOptions={validationOptions.firstName}
                />
              </FormColumn>
              <FormColumn span={12}>
                <TextInput
                  errorMessageRequired="contactForm.errors.lastNameRequired"
                  hasError={errors.lastName !== undefined}
                  label="contactForm.labels.lastName"
                  name="lastName"
                  placeholder="contactForm.placeholders.lastName"
                  register={register}
                  validationOptions={validationOptions.lastName}
                />
              </FormColumn>
              <FormColumn span={12}>
                <TextInput
                  errorMessageRequired="groupReservationForm.errors.groupPhoneRequired"
                  hasError={errors.phone !== undefined}
                  label="groupReservationForm.labels.groupPhone"
                  name="phone"
                  placeholder={t('contactForm.placeholders.phone')}
                  type="tel"
                  register={register}
                  validationOptions={validationOptions.phone}
                />
                {errors.phone && <FormError>{errors.phone.message}</FormError>}
              </FormColumn>
              <FormColumn span={12}>
                <TextInput
                  hasError={errors.mobilePhone !== undefined}
                  label="groupReservationForm.labels.mobilePhone"
                  name="mobilePhone"
                  placeholder={t('contactForm.placeholders.mobilePhone')}
                  type="tel"
                  register={register}
                  validationOptions={validationOptions.mobilePhone}
                />
              </FormColumn>

              <FormColumn span={12}>
                <TextInput
                  errorMessagePattern="contactForm.errors.eMailInvalid"
                  errorMessageRequired="contactForm.errors.eMailRequired"
                  hasError={errors.eMail !== undefined}
                  label="contactForm.labels.eMail"
                  name="eMail"
                  placeholder="contactForm.placeholders.eMail"
                  register={register}
                  validationOptions={validationOptions.eMail}
                />
              </FormColumn>
              <FormColumn span={12}>
                <TextInput
                  label="groupReservationForm.labels.groupStreet"
                  name="groupStreet"
                  placeholder="groupReservationForm.placeholders.groupStreet"
                  register={register}
                  validationOptions={validationOptions.groupStreet}
                  errorMessageRequired="groupReservationForm.errors.groupStreetRequired"
                  hasError={errors.groupStreet !== undefined}
                />
              </FormColumn>
              <FormColumn span={12}>
                <TextInput
                  label={t('groupReservationForm.labels.groupStreetNumber')}
                  name="groupStreetNumber"
                  placeholder={t('groupReservationForm.placeholders.groupStreetNumber')}
                  register={register}
                  validationOptions={validationOptions.groupStreetNumber}
                  errorMessageRequired={t('groupReservationForm.errors.groupStreetNumberRequired')}
                  hasError={errors.groupStreetNumber !== undefined}
                />
              </FormColumn>

              <FormColumn span={12}>
                <TextInput
                  label="groupReservationForm.labels.groupZipCode"
                  name="groupZipCode"
                  placeholder="groupReservationForm.placeholders.groupZipCode"
                  register={register}
                  validationOptions={validationOptions.groupZipCode}
                  errorMessageRequired="groupReservationForm.errors.groupZipCodeRequired"
                  hasError={errors.groupZipCode !== undefined}
                />
              </FormColumn>
              <FormColumn span={12}>
                <TextInput
                  label="groupReservationForm.labels.groupCity"
                  name="groupCity"
                  placeholder="groupReservationForm.placeholders.groupCity"
                  register={register}
                  validationOptions={validationOptions.groupCity}
                  errorMessageRequired="groupReservationForm.errors.groupCityRequired"
                  hasError={errors.groupCity !== undefined}
                />
              </FormColumn>
              <FormColumn span={12}></FormColumn>
              <FormColumn span={12}>
                <SelectInput
                  options={[
                    { value: 'CH', label: 'Schweiz (CH)' },
                    { value: 'DE', label: 'Deutschland (DE)' },
                    { value: 'AT', label: 'Österreich (AT)' },
                    { value: 'FR', label: 'Frankreich (FR)' },
                    { value: 'IT', label: 'Italien (IT)' },
                    { value: 'LI', label: 'Liechtenstein (LI)' },
                    { value: 'BE', label: 'Belgien (BE)' },
                    { value: 'NL', label: 'Niederlande (NL)' },
                    { value: 'LU', label: 'Luxemburg (LU)' },
                    { value: 'GB', label: 'Vereinigtes Königreich (GB)' },
                    { value: 'ES', label: 'Spanien (ES)' },
                    { value: 'PT', label: 'Portugal (PT)' },
                    { value: 'DK', label: 'Dänemark (DK)' },
                    { value: 'NO', label: 'Norwegen (NO)' },
                    { value: 'SE', label: 'Schweden (SE)' },
                    { value: 'FI', label: 'Finnland (FI)' },
                    { value: 'PL', label: 'Polen (PL)' },
                    { value: 'CZ', label: 'Tschechien (CZ)' },
                    { value: 'HU', label: 'Ungarn (HU)' },
                    { value: 'SK', label: 'Slowakei (SK)' },
                    { value: 'SI', label: 'Slowenien (SI)' },
                    { value: 'HR', label: 'Kroatien (HR)' },
                    { value: 'GR', label: 'Griechenland (GR)' },
                    { value: 'IE', label: 'Irland (IE)' },
                    { value: 'IS', label: 'Island (IS)' },
                    { value: 'MT', label: 'Malta (MT)' },
                    { value: 'CY', label: 'Zypern (CY)' },
                    { value: 'EE', label: 'Estland (EE)' },
                    { value: 'LV', label: 'Lettland (LV)' },
                    { value: 'LT', label: 'Litauen (LT)' },
                    { value: 'RO', label: 'Rumänien (RO)' },
                    { value: 'BG', label: 'Bulgarien (BG)' },
                  ]}
                  label={t('contactForm.labels.country')}
                  placeholder={t('contactForm.placeholders.country')}
                  name="country"
                  defaultValue="CH"
                  errorMessageRequired={t('contactForm.errors.countryRequired')}
                  hasError={errors.country !== undefined}
                  register={register}
                  validationOptions={validationOptions.country}
                />
              </FormColumn>

              <FormColumn span={12}>
                <SelectInput
                  options={[
                    { value: 'de', label: t('groupReservationForm.labels.language.de') },
                    { value: 'en', label: t('groupReservationForm.labels.language.en') },
                    { value: 'fr', label: t('groupReservationForm.labels.language.fr') },
                    { value: 'it', label: t('groupReservationForm.labels.language.it') },
                  ]}
                  label={t('groupReservationForm.labels.languageTitle')}
                  placeholder={t('groupReservationForm.placeholders.selectLanguage')}
                  name="language"
                  defaultValue="de"
                  errorMessageRequired={t('groupReservationForm.errors.languageRequired')}
                  hasError={errors.language !== undefined}
                  register={register}
                  validationOptions={validationOptions.language}
                />
              </FormColumn>
              <Heading skin={getHeadingSkin('heading3')} elementType={getHeadingTag('heading3')}>
                {t('tableReservationForm.labels.detailVisit')}
              </Heading>
              <FormColumn span={12}>
        <SelectInput
  options={optionsExperienceType}
  name="experienceType"
  label="groupReservationForm.labels.experienceTypeTitle"
  placeholder="groupReservationForm.placeholders.experienceType"
  register={register}
  validationOptions={validationOptions.experienceType}
/>


              </FormColumn>
              <DateGroupStyled>
                <FormColumn span={6}>
                  <StyledLabel htmlFor="visitDate">
                    {t('tableReservationForm.labels.visitDate')}
                  </StyledLabel>

                  <FormDatePicker
                    name="visitDate"
                    selected={startDate}
                    onChange={(date) => {
                      setStartDate(date);
                      setValue('visitDate', date!);
                    }}
                    placeholderText=""
                    minDate={new Date(new Date().setDate(new Date().getDate() + 4))} // ✅ block earlier dates
                    validationOptions={validationOptions.visitDate}
                    errorMessageRequired="tableReservationForm.errors.visitDateRequired"
                    hasError={errors.visitDate !== undefined}
                  />
                </FormColumn>

                <FormColumn span={6}>
                  <SelectInput
                    label={t('tableReservationForm.labels.visitTime')}
                    name="visitTime"
                    placeholder={t('tableReservationForm.placeholders.visitTime')}
                    errorMessageRequired="tableReservationForm.errors.visitTimeRequired"
                    hasError={errors.visitTime !== undefined}
                    register={register}
                    validationOptions={validationOptions.visitTime}
                    className="time-dropdown"
                    options={generateTimeGridOptions()}
                  />
                </FormColumn>
              </DateGroupStyled>
              <FormColumn span={12}>
                <TextInput
                  label="groupReservationForm.labels.groupName"
                  name="groupName"
                  placeholder="groupReservationForm.placeholders.groupName"
                  register={register}
                  hasError={errors.groupName !== undefined}
                  errorMessageRequired="groupReservationForm.errors.groupNameRequired"
                  validationOptions={{ required: true }}
                />
              </FormColumn>

              <FormColumn span={12}>
                <Heading skin={getHeadingSkin('heading3')} elementType={getHeadingTag('heading3')}>
                  {t('schulenForm.labels.arrivalPlaceTitle')}
                </Heading>
                <WrapperRBArrivalPlace>
                  <RadioInput
                    id="arrivalPlaceWest"
                    name="arrivalPlace"
                    register={register}
                    translation="schulenForm.labels.arrivalPlace.entranceWest"
                    value="Eingang West"
                  />
                  <RadioInput
                    id="arrivalPlaceEast"
                    name="arrivalPlace"
                    register={register}
                    translation="schulenForm.labels.arrivalPlace.entranceEast"
                    value="Eingang Ost"
                    validationOptions={validationOptions.arrivalPlace}
                  />
                </WrapperRBArrivalPlace>
                {errors.arrivalPlace && isSubmitted && (
                  <FormError className="arrivalPlaceError">
                    {t('schulenForm.errors.arrivalPlaceRequired')}
                  </FormError>
                )}
              </FormColumn>
              <Heading skin={getHeadingSkin('heading3')} elementType={getHeadingTag('heading3')}>
                {t('schulenForm.labels.numberOfEntries')}
              </Heading>
              <WrapperRBNumberOfEntries>
                <FormColumn span={12}>
                  <TextInput
                    errorMessageRequired="schulenForm.errors.adultRequired"
                    hasError={errors.adult !== undefined}
                    label="schulenForm.labels.adult"
                    name="adult"
                    placeholder="schulenForm.placeholders.adult"
                    register={register}
                    validationOptions={validationOptions.adult}
                  />
                </FormColumn>
                {/* 
<FormColumn span={12}>
  <TextInput
    errorMessageRequired="schulenForm.errors.adultMemberPlusRequired"
    hasError={errors.adultMemberPlus !== undefined}
    label="schulenForm.labels.adultMemberPlus"
    name="adultMemberPlus"
    placeholder="schulenForm.placeholders.adultMemberPlus"
    register={register}
    validationOptions={validationOptions.adultMemberPlus}
  />
</FormColumn>
*/}

                <FormColumn span={12}>
                  <TextInput
                    errorMessageRequired="schulenForm.errors.childOver6Required"
                    hasError={errors.childOver6 !== undefined}
                    label="schulenForm.labels.childOver6"
                    name="childOver6"
                    placeholder="schulenForm.placeholders.childOver6"
                    register={register}
                    validationOptions={validationOptions.childOver6}
                  />
                </FormColumn>
                <FormColumn span={12}>
                  <TextInput
                    errorMessageRequired="schulenForm.errors.childUnder6Required"
                    hasError={errors.childUnder6 !== undefined}
                    label="schulenForm.labels.childUnder6"
                    name="childUnder6"
                    placeholder="schulenForm.placeholders.childUnder6"
                    register={register}
                    validationOptions={validationOptions.childUnder6}
                  />
                </FormColumn>
                {/* 
<FormColumn span={12}>
  <TextInput
    errorMessageRequired="schulenForm.errors.railAwayRequired"
    hasError={errors.railAway !== undefined}
    label="schulenForm.labels.railAway"
    name="railAway"
    placeholder="schulenForm.placeholders.railAway"
    register={register}
    validationOptions={validationOptions.railAway}
  />
</FormColumn>
*/}
              </WrapperRBNumberOfEntries>
              {/* 
              <FormColumn span={12}>
                <Heading skin={getHeadingSkin('heading3')} elementType={getHeadingTag('heading3')}>
                  {t('groupReservationForm.labels.mealsTitle')}
                </Heading>
                <WrapperRBPayment>
                  <RadioInput
                    id="mealsYes"
                    name="meals"
                    register={register}
                    translation="groupReservationForm.labels.meals.yes"
                    value="Ja"
                  />
                  <RadioInput
                    id="mealsNo"
                    name="meals"
                    register={register}
                    translation="groupReservationForm.labels.meals.no"
                    value="Nein"
                    validationOptions={validationOptions.meals}
                  />
                </WrapperRBPayment>
                {errors.meals && isSubmitted && (
                  <FormError className="mealsError">
                    {t('groupReservationForm.errors.mealsRequired')}
                  </FormError>
                )}



              </FormColumn>



              <FormColumn span={12}>
                <SelectInput
                  options={optionsReasonVisit}
                  label="groupReservationForm.labels.reasonVisitTitle"
                  placeholder="tableReservationForm.placeholders.selectReasonVisit"
                  name="reasonVisit"
                  register={register}
                />
              </FormColumn>
              <FormColumn span={12}>
                <SelectInput
                  options={optionsDesiredLocation}
                  label="groupReservationForm.labels.desiredLocationTitle"
                  placeholder="tableReservationForm.placeholders.desiredLocation"
                  name="desiredLocation"
                  register={register}
                />
              </FormColumn>
              <FormColumn span={12}>
                <TextInput
                  label="groupReservationForm.labels.mealTime"
                  name="mealTime"
                  placeholder="groupReservationForm.placeholders.mealTime"
                  register={register}
                />
              </FormColumn>
          */}
              <FormColumn span={12}>
                <HeadlineLabel htmlFor="message">
                  {t('tableReservationForm.labels.yourMessage')}
                </HeadlineLabel>
                <InputTextArea
                  {...register('message')}
                  id="message"
                  placeholder={t('tableReservationForm.placeholders.yourMessage')}
                />
              </FormColumn>
              <FormColumn span={12}>
                <CheckboxInput
                  name="newsletterSubscribe"
                  register={register}
                  translation="contactForm.labels.newsletterSubscribe"
                  url={urlNewsletter}
                />
              </FormColumn>
              <FormColumn span={12}>
                {!isSubmitting && (
                  <InputSubmit
                    disabled={isSubmitting}
                    type="submit"
                    value={t('contactForm.labels.submit')}
                  />
                )}
                {isSubmitting && <Loading />}
              </FormColumn>
              <FormColumn span={12}>
                <Copytext textstring={t('forms.requiredFields')} />
              </FormColumn>
              <input type="hidden" value={lang} {...register('locale')} />
              <input type="hidden" value="11-GruppenReservationen" {...register('category')} />
              <input
  type="hidden"
  {...register("programId")}
  value={selectedExperience || ""}
/>

<input
  type="hidden"
  {...register("programName")}
  value={experienceTypeObject[selectedExperience] || ""}
/>

            </form>
          </Column>
        </>
      )}
    </>
  );
};
